/**
 * Comparison range for the Sales Projection dialog: the same four numbers as the Projection Basis
 * (date range, days, total sales, daily average) for any other date range, next to how far that
 * average sits from the one the forecast is using. Read-only: it never changes the forecast.
 */

import React, { useMemo, useState } from "react";
import moment from "moment";
import { Alert, Box, Button, CircularProgress, Paper, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import InventoryService, { SalesRange } from "services/InventoryService";
import { formatNumber } from "../../utils";

const DATE_FORMAT = "YYYY-MM-DD";

interface ComparisonSalesRangeProps {
  inventoryId: string;
  country: string;
  /** The projection basis being compared against */
  basisStart: string;
  basisEnd: string;
  basisDailyAverage: number;
  /** The daily rate this product row is being what-if forecast with, if any */
  trialUnitsPerDay?: number;
  onTrialUnitsPerDayChange: (unitsPerDay: number | null) => void;
}

export default function ComparisonSalesRange({
  inventoryId,
  country,
  basisStart,
  basisEnd,
  basisDailyAverage,
  trialUnitsPerDay,
  onTrialUnitsPerDayChange,
}: ComparisonSalesRangeProps): JSX.Element {
  const inventoryService = useMemo(() => new InventoryService(), []);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [result, setResult] = useState<SalesRange | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const yesterday = useMemo(() => moment().subtract(1, "days"), []);
  const isRangeBackwards = startDate != null && endDate != null && startDate > endDate;

  const compare = async (rangeStart: string, rangeEnd: string) => {
    setIsLoading(true);
    setErrorMessage(null);
    try {
      setResult(await inventoryService.getSalesRange({ inventoryId, country, startDate: rangeStart, endDate: rangeEnd }));
    } catch (error: any) {
      setResult(null);
      setErrorMessage(error?.message || String(error));
    } finally {
      setIsLoading(false);
    }
  };

  const compareSamePeriodLastYear = () => {
    const lastYearStart = moment(basisStart, DATE_FORMAT).subtract(1, "years").format(DATE_FORMAT);
    const lastYearEnd = moment(basisEnd, DATE_FORMAT).subtract(1, "years").format(DATE_FORMAT);
    setStartDate(lastYearStart);
    setEndDate(lastYearEnd);
    compare(lastYearStart, lastYearEnd);
  };

  const changeVsBasis = result && basisDailyAverage > 0
    ? (result.dailyAverage - basisDailyAverage) / basisDailyAverage
    : null;

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        Compare with another date range
      </Typography>
      <Typography variant="caption" sx={{ opacity: 0.7, display: "block", mb: 1.5 }}>
Comparing changes nothing. "Use data to simulate forecast" tries the range's daily average as a what-if.
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", mb: result || errorMessage ? 2 : 0 }}>
        <DatePicker
          label="From"
          value={startDate ? moment(startDate) : null}
          maxDate={yesterday}
          onChange={(newValue) => setStartDate(newValue && newValue.isValid() ? newValue.format(DATE_FORMAT) : null)}
          slotProps={{ textField: { size: "small", sx: { width: 160 } } }}
        />
        <DatePicker
          label="To"
          value={endDate ? moment(endDate) : null}
          maxDate={yesterday}
          onChange={(newValue) => setEndDate(newValue && newValue.isValid() ? newValue.format(DATE_FORMAT) : null)}
          slotProps={{ textField: { size: "small", sx: { width: 160 }, error: isRangeBackwards, helperText: isRangeBackwards ? "Ends before it starts" : undefined } }}
        />
        <Button
          variant="contained"
          size="small"
          disabled={!startDate || !endDate || isRangeBackwards || isLoading}
          onClick={() => startDate && endDate && compare(startDate, endDate)}
        >
          Compare
        </Button>
        <Button size="small" disabled={isLoading} onClick={compareSamePeriodLastYear} sx={{ textTransform: "none" }}>
          Same dates last year
        </Button>
        {isLoading && <CircularProgress size={18} />}
      </Box>

      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

      {result && (
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 2 }}>
          <Box>
            <Typography variant="caption" sx={{ opacity: 0.7, display: "block" }}>Date Range</Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {moment(result.startDate).format("MMM D")} - {moment(result.endDate).format("MMM D, YYYY")}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ opacity: 0.7, display: "block" }}>Days in Range</Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>{result.daysInRange}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ opacity: 0.7, display: "block" }}>Total Sales</Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatNumber(Math.round(result.totalSales))}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ opacity: 0.7, display: "block" }}>Daily Average</Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {result.dailyAverage.toFixed(1)} units/day
              {changeVsBasis != null && (
                <Box
                  component="span"
                  sx={{ ml: 1, fontWeight: 600, color: changeVsBasis >= 0 ? "rgb(16, 122, 87)" : "rgb(180, 64, 0)" }}
                >
                  {changeVsBasis >= 0 ? "+" : ""}{(changeVsBasis * 100).toFixed(0)}% vs basis
                </Box>
              )}
            </Typography>
          </Box>
        </Box>
      )}

      {(result || trialUnitsPerDay !== undefined) && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", mt: 2 }}>
          {result && (
            <Button
              variant="outlined"
              size="small"
              sx={{ textTransform: "none" }}
              disabled={trialUnitsPerDay === result.dailyAverage}
              onClick={() => onTrialUnitsPerDayChange(result.dailyAverage)}
            >
              Use data to simulate forecast
            </Button>
          )}
          {trialUnitsPerDay !== undefined && (
            <>
              <Typography variant="caption">
                Simulating at <strong>{trialUnitsPerDay.toFixed(1)} units/day</strong> — see the what-if below.
              </Typography>
              <Button size="small" sx={{ textTransform: "none" }} onClick={() => onTrialUnitsPerDayChange(null)}>Stop simulating</Button>
            </>
          )}
        </Box>
      )}
    </Paper>
  );
}
